import type { TMessageData } from "@asurion-core/headless-chat-web";
import type { Logger } from "@expert/logging";

import { MESSAGE_DATA_TYPES, SimplrChatSDK } from "@asurion-core/headless-chat-web";

import type { AnalyticsInfo, SessionMetadata } from "../store";
import type { GuideSelectionObject } from "./types";

import { toSimplrTicketId } from "./simplrTicketId";

export function navigateBack({ restartFlow = false }: { restartFlow?: boolean }) {
    if (restartFlow) {
        void SimplrChatSDK.navigateBack({ startOver: true });
    } else {
        void SimplrChatSDK.navigateBack();
    }
}

/*
   Calling startSession for a sessionId with an existing session will not
   restart the session, it will pick back up wherever the session left off.
   This allows for managing multiple sessions at a given time. sessionId is the
   workspace id; it is suffixed here so it cannot collide with another flow in
   the Simplr system (like IVR).

   The idle (off-call) session sends no externalTicketId. It lives up to 24h, so its room
   can time out; with no ticket id, Simplr opens a fresh ticket instead of replaying the old
   one's history. https://asurion-teams.slack.com/archives/C03C5T941S8/p1792153998184059

   Expert University training sessions send none either: they don't need tracking in the logic
   builder, and their ids are too long for Simplr's 50-character externalTicketId limit once suffixed.
*/
export function startSimplrSession({
    sessionId,
    metadata,
    omitExternalTicketId = false,
}: {
    sessionId: string;
    metadata?: SessionMetadata;
    omitExternalTicketId?: boolean;
}) {
    const externalTicketId = omitExternalTicketId ? undefined : toSimplrTicketId(sessionId);
    void SimplrChatSDK.startSession({ externalTicketId, moreTicketInfo: metadata });
}

/*
    for our use case, we can not use startSession to store moreTicketInfo in
    the logic builder because the Simplr team ignores back to back startSession
    calls for the same sessionId. Instead, we use sendClientMetaData to update
    the moreTicketInfo for an existing session and store it in logic builder.
*/
export function sendMetadata({ metadata }: { metadata?: SessionMetadata }) {
    void SimplrChatSDK.sendClientMetaData({ moreTicketInfo: metadata });
}

/*
    `text` becomes `interaction.data.text` in the logic builder. For most steps it is the clicked option's value.
    For a free-text step it is what the expert typed (the chat server sanitizes `data.text`); pass `isFreeText`
    so that value (possible customer PII) is kept out of the logs, and only its length is logged.
*/
export function triggerNextStep(
    text: string,
    logger: Logger,
    onBeforeSend?: () => void,
    { isFreeText = false }: { isFreeText?: boolean } = {},
) {
    const messageData = {
        type: MESSAGE_DATA_TYPES.TEXT,
        text,
    } as TMessageData;

    if (isFreeText) {
        logger.info({ freeTextLength: text.length }, "Solve Guide | calling triggerNextStep with free text");
    } else {
        logger.info(`Solve Guide | calling triggerNextStep with: ${text}`);
    }
    if (onBeforeSend) {
        onBeforeSend();
    }
    void SimplrChatSDK.sendMessage(messageData);
}

export function automaticallySelectGuideIfNeeded(
    analyticsInfo: AnalyticsInfo,
    availableGuides: GuideSelectionObject[],
    guide: string,
    logger: Logger,
    onBeforeSend?: () => void,
) {
    if (
        availableGuides.map((guide) => guide.title)?.includes(guide) &&
        analyticsInfo.currentStepId === "GuideSelection"
    ) {
        triggerNextStep(guide, logger, onBeforeSend);
        logger.info(`Solve Guide | Selected flow automatically: ${guide}`);
    } else {
        logger.info({ availableGuides, ...analyticsInfo }, `Solve Guide | NOT selecting flow automatically: ${guide}`);
    }
}
